import { apiClient } from '../client';
import type { ApiError } from '@/lib/types/errors';
import { err, ok, type Result } from '@/lib/types/result';

export type FeatureRequestFilter = 'all' | 'liked' | 'disliked' | 'mine';
export type FeatureRequestSort = 'votes' | 'recent';

export interface FeatureRequestV2 {
  id: string;
  /** Feature request title/content. Validation: min 3 words, max 100 chars */
  content: string;
  description?: string;
  createdAt: string; // RFC3339 format: "2026-03-17T14:30:00+08:00"
  likedCount: number;
  dislikedCount: number;
  userVote: 'liked' | 'disliked' | null;
  isOwner: boolean;
}

export interface FeatureRequestsResponse {
  featureRequests: FeatureRequestV2[];
  filter: FeatureRequestFilter;
  sort: FeatureRequestSort;
  hasMore: boolean;
  cursor?: string;
}

/**
 * Next-page cursor for useInfiniteQuery.getNextPageParam. The server cursor is
 * opaque and sort-aware; the client must echo it back verbatim and only when
 * hasMore says a next page exists. Deriving a cursor client-side (e.g. the old
 * cumulative item count) breaks keyset pagination and 500s the server (#843).
 */
export const nextFeatureRequestPageParam = (
  lastPage: Pick<FeatureRequestsResponse, 'hasMore' | 'cursor'>
): string | undefined => (lastPage.hasMore ? lastPage.cursor : undefined);

/**
 * API response wrapper from v2 endpoints
 */

interface GetFeatureRequestsParams {
  filter?: FeatureRequestFilter;
  sort?: FeatureRequestSort;
  limit?: number;
  cursor?: string;
}

export const getFeatureRequests = async (
  params: GetFeatureRequestsParams = {}
): Promise<Result<FeatureRequestsResponse, ApiError>> => {
  const { filter = 'all', sort = 'votes', limit = 20, cursor } = params;

  try {
    const requestParams: Record<string, string | number> = { filter, sort, limit };
    if (cursor !== undefined) {
      requestParams.cursor = cursor;
    }

    const response = await apiClient.get<FeatureRequestsResponse>('/v2/feature-requests', {
      params: requestParams,
    });

    if (!response.ok) {
      return err(response.error);
    }

    return ok(normalizeListResponse(response.value));
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to fetch feature requests',
    });
  }
};

/**
 * Create a new feature request
 * @param content - Feature request title. Validation: min 3 words, max 100 chars
 * @param description - Detailed description. Validation: min 5 words, max 2000 chars
 */
export const createFeatureRequest = async (
  content: string,
  description: string
): Promise<Result<FeatureRequestV2, ApiError>> => {
  try {
    const response = await apiClient.post<FeatureRequestV2>('/v2/feature-requests', {
      content,
      description,
    });

    if (!response.ok) {
      return err(response.error);
    }

    return ok(response.value);
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to create feature request',
    });
  }
};

// ============================================================
// Detail / mutation surface + featureRequestsApiV2 object
// Relocated verbatim from the former root lib/api/feature-requests.ts.
// Both getFeatureRequests impls normalize userVote (upvote->liked,
// downvote->disliked) to honor the FeatureRequestV2.userVote type. The
// backend enum is "upvote"/"downvote" (feature_request.go); the list UI
// only consumes likedCount/dislikedCount, so this is defense-in-depth.
// ============================================================

// Detail response type
export interface FeatureRequestDetailV2 {
  id: string;
  content: string;
  description: string;
  createdAt: string;
  updatedAt?: string;
  likedCount: number;
  dislikedCount: number;
  userVote: 'liked' | 'disliked' | null;
  isOwner: boolean;
}

// Normalize backend userVote to frontend format
export function normalizeUserVote(backendVote: string | null): 'liked' | 'disliked' | null {
  if (!backendVote) return null;
  if (backendVote === 'liked' || backendVote === 'like' || backendVote === 'upvote') return 'liked';
  if (backendVote === 'disliked' || backendVote === 'unlike' || backendVote === 'downvote')
    return 'disliked';
  return null;
}

// Normalize a single feature request
function normalizeFeatureRequest(request: FeatureRequestV2): FeatureRequestV2 {
  return {
    ...request,
    userVote: normalizeUserVote(request.userVote),
  };
}

// Normalize API response
function normalizeDetailResponse(response: FeatureRequestDetailV2): FeatureRequestDetailV2 {
  return {
    ...response,
    userVote: normalizeUserVote(response.userVote),
  };
}

// Normalize list response
export function normalizeListResponse(response: FeatureRequestsResponse): FeatureRequestsResponse {
  return {
    ...response,
    featureRequests: response.featureRequests.map(normalizeFeatureRequest),
  };
}

// Request types
export interface VoteFeatureRequestRequest {
  vote: 'like' | 'unlike' | 'none';
}

// Vote endpoint returns only aggregate counts + the caller's own vote, NOT the
// full detail object (feature_request_handler.go VoteFeatureRequest ->
// {likedCount, dislikedCount, userVote}). Kept narrow so the type cannot drift
// from the server response shape.
export interface VoteFeatureRequestResponse {
  likedCount: number;
  dislikedCount: number;
  userVote: 'liked' | 'disliked' | null;
}

export interface UpdateFeatureRequestRequest {
  content: string;
  description: string;
}

// API response wrapper
interface ApiResponse<T> {
  data: T;
  success: boolean;
  timestamp: string;
  request_id?: string;
}

// V2 API implementation (object form — used by detail/edit/form consumers)
export const featureRequestsApiV2 = {
  /**
   * Get a single feature request by ID
   */
  getFeatureRequestById: async (id: string): Promise<Result<FeatureRequestDetailV2, ApiError>> => {
    try {
      const response = await apiClient.get<FeatureRequestDetailV2>(`/v2/feature-requests/${id}`);

      if (!response.ok) {
        return err(response.error);
      }

      return ok(normalizeDetailResponse(response.value));
    } catch (error) {
      return err({
        type: 'network',
        message: error instanceof Error ? error.message : 'Failed to fetch feature request',
      });
    }
  },

  /**
   * Update a feature request
   */
  updateFeatureRequest: async (
    id: string,
    data: UpdateFeatureRequestRequest
  ): Promise<Result<FeatureRequestDetailV2, ApiError>> => {
    try {
      const response = await apiClient.put<FeatureRequestDetailV2>(
        `/v2/feature-requests/${id}`,
        data
      );

      if (!response.ok) {
        return err(response.error);
      }

      return ok(response.value);
    } catch (error) {
      return err({
        type: 'network',
        message: error instanceof Error ? error.message : 'Failed to update feature request',
      });
    }
  },

  /**
   * Delete a feature request
   */
  deleteFeatureRequest: async (id: string): Promise<Result<void, ApiError>> => {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(`/v2/feature-requests/${id}`);

      if (!response.ok) {
        return err(response.error);
      }

      return ok(undefined);
    } catch (error) {
      return err({
        type: 'network',
        message: error instanceof Error ? error.message : 'Failed to delete feature request',
      });
    }
  },

  /**
   * Vote on a feature request
   */
  voteFeatureRequest: async (
    id: string,
    data: VoteFeatureRequestRequest
  ): Promise<Result<VoteFeatureRequestResponse, ApiError>> => {
    try {
      const response = await apiClient.post<VoteFeatureRequestResponse>(
        `/v2/feature-requests/${id}/vote`,
        data
      );

      if (!response.ok) {
        return err(response.error);
      }

      // Map exactly the fields the server sends; normalize userVote to match
      // the rest of the detail/list surface.
      return ok({
        likedCount: response.value.likedCount,
        dislikedCount: response.value.dislikedCount,
        userVote: normalizeUserVote(response.value.userVote),
      });
    } catch (error) {
      return err({
        type: 'network',
        message: error instanceof Error ? error.message : 'Failed to vote on feature request',
      });
    }
  },
};
